import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../../App';
import Modal from './Modal';
import { money } from '../../utils/format';
import { canViewCosts } from '../../utils/permissions';
import { Package, MapPin, Tag, Truck, DollarSign, Box, FileText, Image } from 'lucide-react';

export default function ProductDetail({ productId, onClose }) {
  const { user } = useAuth();
  const showCosts = canViewCosts(user);
  const [product, setProduct] = useState(null);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    Promise.all([
      api('/api/products/' + productId),
      api('/api/products/' + productId + '/stock'),
    ]).then(([pr, sr]) => {
      setProduct(pr.data);
      setStock(sr.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  if (!productId) return null;
  if (loading) return (
    <Modal title="Product Details" onClose={onClose}>
      <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>Loading...</div>
    </Modal>
  );
  if (!product) return null;

  const totalStock = stock.reduce((s, r) => s + Number(r.current_stock), 0);
  const totalValue = stock.reduce((s, r) => s + Number(r.current_stock) * Number(product.unit_cost), 0);

  return (
    <Modal title="Product Details" onClose={onClose}>
      <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
        {/* Photo */}
        <div style={{
          width: 120, height: 120, borderRadius: 14, flexShrink: 0, overflow: 'hidden',
          background: 'var(--navy)', border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {product.photo_url ? (
            <img src={product.photo_url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Package size={36} style={{ color: 'var(--faint)', opacity: .3 }} />
          )}
        </div>

        {/* Header info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: 'Lora,serif', fontSize: 20, margin: '0 0 6px', color: 'var(--text)' }}>{product.name}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {product.category_name && (
              <span className="cat-badge" style={{ background: (product.category_color || '#888') + '20', color: product.category_color }}>{product.category_name}</span>
            )}
            {!product.is_active && <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: 'rgba(231,76,60,.12)', color: 'var(--danger)' }}>ARCHIVED</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 13 }}>
            <div style={{ color: 'var(--muted)' }}><Tag size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />SKU: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{product.sku || '-'}</span></div>
            <div style={{ color: 'var(--muted)' }}><DollarSign size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />Cost: <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{showCosts ? money(product.unit_cost) : '•••'}</span></div>
            <div style={{ color: 'var(--muted)' }}><Box size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />{product.unit_type}{product.unit_size ? ' (' + product.unit_size + ')' : ''}</div>
            <div style={{ color: 'var(--muted)' }}><Truck size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />{product.supplier_name || 'No supplier'}</div>
          </div>
        </div>
      </div>

      {product.description && (
        <div style={{ padding: '12px 16px', background: 'var(--navy)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20, fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
          {product.description}
        </div>
      )}

      {/* Summary stats */}
      <div style={{ display: 'flex', gap: 1, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ flex: 1, padding: '14px 12px', textAlign: 'center', background: 'var(--card)' }}>
          <div style={{ fontFamily: 'Lora,serif', fontSize: 20, fontWeight: 700, color: 'var(--gold)' }}>{Math.round(totalStock)}</div>
          <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', marginTop: 3 }}>Total Stock</div>
        </div>
        <div style={{ flex: 1, padding: '14px 12px', textAlign: 'center', background: 'var(--card)', borderLeft: '1px solid var(--border)' }}>
          <div style={{ fontFamily: 'Lora,serif', fontSize: 20, fontWeight: 700, color: '#2ECC71' }}>{showCosts ? money(totalValue) : '•••'}</div>
          <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', marginTop: 3 }}>Total Value</div>
        </div>
        <div style={{ flex: 1, padding: '14px 12px', textAlign: 'center', background: 'var(--card)', borderLeft: '1px solid var(--border)' }}>
          <div style={{ fontFamily: 'Lora,serif', fontSize: 20, fontWeight: 700, color: '#78A8E8' }}>{stock.length}</div>
          <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', marginTop: 3 }}>Locations</div>
        </div>
      </div>

      {/* Stock by location */}
      {stock.length > 0 && (
        <div>
          <h4 style={{ fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Stock by Location</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {stock.map(s => {
              const pct = s.par_max > 0 ? Number(s.current_stock) / Number(s.par_max) : 0;
              const isLow = Number(s.current_stock) <= Number(s.reorder_point);
              const isOut = Number(s.current_stock) === 0;
              return (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  background: 'var(--navy)', borderRadius: 10, border: '1px solid var(--border)',
                  borderLeft: '3px solid ' + (isOut ? '#E74C3C' : isLow ? '#F39C12' : '#2ECC71'),
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.location_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>
                      Par: {Number(s.par_min).toFixed(0)}–{Number(s.par_max).toFixed(0)} &middot; Reorder at {Number(s.reorder_point).toFixed(0)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 50, height: 5, borderRadius: 3, background: 'var(--card)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: Math.min(pct * 100, 100) + '%', borderRadius: 3, background: isOut ? '#E74C3C' : isLow ? '#F39C12' : '#2ECC71' }} />
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: isOut ? '#E74C3C' : isLow ? '#F39C12' : 'var(--text)', minWidth: 30, textAlign: 'right' }}>
                      {Number(s.current_stock).toFixed(0)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stock.length === 0 && (
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--faint)' }}>
          <MapPin size={20} style={{ opacity: .3, marginBottom: 6 }} />
          <p>Not assigned to any locations yet</p>
        </div>
      )}
    </Modal>
  );
}
